import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router";
import {
  filtrosParaBusca,
  filtrosRelatorioSchema,
  FORMA_PAGAMENTO_ROTULO,
  formatarDataHora,
  formatarReais,
  type FiltrosRelatorio,
} from "@caixaflow/shared";
import { api } from "../api/cliente";
import { useSessao } from "../api/sessao";
import type { OpcoesFiltro, Pagina, VendaRelatorio } from "../api/tipos";
import { Carregando, FalhaAoCarregar } from "../components/Estados";
import { PainelFiltros } from "../components/FiltrosRelatorio";
import { ModalVenda } from "../components/ModalVenda";

// Aqui o padrão é ver tudo (válidas e canceladas); o Fechamento olha só as válidas.
const PADRAO: FiltrosRelatorio = { status: "TODAS" };

function filtrosDaUrl(parametros: URLSearchParams): FiltrosRelatorio {
  const r = filtrosRelatorioSchema.safeParse(Object.fromEntries(parametros));
  if (!r.success) return PADRAO;
  return parametros.has("status") ? r.data : { ...r.data, status: "TODAS" };
}

/** Busca com a situação sempre explícita (sem ela, esta tela entende "todas"). */
function buscaDe(f: FiltrosRelatorio): string {
  const busca = filtrosParaBusca(f);
  return f.status === "VALIDA" ? `${busca}${busca ? "&" : ""}status=VALIDA` : busca;
}

/**
 * Todas as vendas, com os filtros do Fechamento e busca pelo número. Tocar numa venda abre o
 * detalhe, com cancelar e ajustar. O operador vê só as próprias vendas (a API garante).
 */
export function PaginaVendas() {
  const { data: sessao } = useSessao();
  const souAdmin = sessao?.usuario.perfil === "ADMIN";
  const [parametros, setParametros] = useSearchParams();
  const [filtros, setFiltros] = useState<FiltrosRelatorio>(() => filtrosDaUrl(parametros));
  const [pagina, setPagina] = useState(1);
  const [aberta, setAberta] = useState<string | null>(null);
  const busca = buscaDe(filtros);

  useEffect(() => {
    if (busca !== parametros.toString()) setParametros(new URLSearchParams(busca), { replace: true });
  }, [busca, parametros, setParametros]);

  const definir = (mudanca: Partial<FiltrosRelatorio>) => {
    setPagina(1);
    setFiltros((atual) => ({ ...atual, ...mudanca }));
  };

  const opcoes = useQuery({
    queryKey: ["relatorio-opcoes", filtros.evento?.join(",") ?? ""],
    queryFn: () => api<OpcoesFiltro>(`/relatorios/opcoes${filtros.evento?.length ? `?evento=${filtros.evento.join(",")}` : ""}`),
    placeholderData: keepPreviousData,
  });
  const vendas = useQuery({
    queryKey: ["relatorio-vendas", busca, pagina],
    queryFn: () => api<Pagina<VendaRelatorio>>(`/relatorios/vendas?${busca}${busca ? "&" : ""}pagina=${pagina}&porPagina=30`),
    placeholderData: keepPreviousData,
  });
  const totalPaginas = vendas.data ? Math.max(1, Math.ceil(vendas.data.total / vendas.data.porPagina)) : 1;
  const semStatus = filtrosParaBusca({ ...filtros, status: "VALIDA" });

  return (
    <>
      <PainelFiltros
        filtros={filtros}
        opcoes={opcoes.data}
        souAdmin={souAdmin}
        definir={definir}
        limpar={() => {
          setPagina(1);
          setFiltros(PADRAO);
        }}
        comNumero
        statusPadrao="TODAS"
      />

      <div className="linha">
        <span className="mudo pequeno">
          {vendas.data && `${vendas.data.total} ${vendas.data.total === 1 ? "venda" : "vendas"}`}
          {vendas.isFetching && " · atualizando…"}
        </span>
        <Link className="btn pequeno fantasma" to={`/fechamento${semStatus ? `?${semStatus}` : ""}`}>
          Ver totais no Fechamento
        </Link>
      </div>

      {vendas.isPending && <Carregando />}
      {vendas.error && <FalhaAoCarregar erro={vendas.error} tentarDeNovo={() => vendas.refetch()} />}
      {vendas.data?.itens.length === 0 && <div className="card vazio">Nenhuma venda nestes filtros.</div>}
      {!!vendas.data?.itens.length && (
        <div className="card">
          {vendas.data.itens.map((v) => (
            <LinhaVenda key={v.id} venda={v} mostrarOperador={souAdmin} onAbrir={() => setAberta(v.id)} />
          ))}
        </div>
      )}
      {totalPaginas > 1 && (
        <div className="linha">
          <button className="btn pequeno fantasma" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)}>
            Mais recentes
          </button>
          <span className="mudo pequeno">
            Página {pagina} de {totalPaginas}
          </span>
          <button className="btn pequeno fantasma" disabled={pagina >= totalPaginas} onClick={() => setPagina((p) => p + 1)}>
            Anteriores
          </button>
        </div>
      )}

      <ModalVenda vendaId={aberta} onFechar={() => setAberta(null)} />
    </>
  );
}

/** Uma venda nas listas (Vendas e Fechamento): toque em "Ver" para o detalhe e as ações. */
export function LinhaVenda({ venda: v, mostrarOperador, onAbrir }: { venda: VendaRelatorio; mostrarOperador: boolean; onAbrir: () => void }) {
  return (
    <div className={`venda-linha${v.status === "CANCELADA" ? " cancelada" : ""}`}>
      <span className="t">{formatarDataHora(v.criadaEm)}</span>
      <span className="d">
        #{v.numero} · {v.itens.map((i) => `${i.quantidade}× ${i.nome}`).join(", ")}
        <span className="chip">{FORMA_PAGAMENTO_ROTULO[v.formaPagamento]}</span>
        {v.status === "CANCELADA" && <span className="chip perigo">cancelada</span>}
        {v.ajustada && <span className="chip alerta">ajustada</span>}
        <div className="mudo pequeno">
          Caixa #{v.caixa}
          {v.caixaStatus === "FECHADO" && " (fechado)"}
          {mostrarOperador && ` · ${v.operador}`}
          {v.cancelamento?.motivo && ` · cancelada: ${v.cancelamento.motivo}`}
        </div>
      </span>
      <span className="v">{formatarReais(v.totalCentavos)}</span>
      <button className="link-botao" onClick={onAbrir} aria-label={`Ver venda #${v.numero}`}>
        Ver
      </button>
    </div>
  );
}
